// Order Drafting typing shortcuts (abbreviation → expansion).
//
// The list is fetched from the backend (GET /bac/order-shortcuts/) 
// Expansion is auto-triggered on a SPACE after the abbreviation (case-
// insensitive), and performed as a single undo step so Ctrl+Z restores the
// abbreviation. Shared by both Order Drafting editors (admin + arbitrator).

import { Extension, InputRule } from "@tiptap/core";
import instance from "api/globalaxios";

export async function fetchOrderShortcuts() {
	try {
		const res = await instance.get("/bac/order-shortcuts/");
		return Array.isArray(res.data) ? res.data : [];
	} catch (err) {
		console.error("Failed to load order shortcuts:", err);
		return [];
	}
}

// { "cl": "Claimant", ... } keyed on the lower-cased abbreviation, for
// case-insensitive matching.
export function buildShortcutMap(list) {
	const map = {};
	(list || []).forEach((s) => {
		if (s && s.abbreviation) {
			map[String(s.abbreviation).toLowerCase()] = s.expansion || "";
		}
	});
	return map;
}

// Auto-expand the word before the caret when the drafter types a space.
// `getMap` is a function (not a value) so the editor can be wired up before the
// list has finished loading — it reads the latest map on each keystroke.
export function attachShortcutExpansion(editor, getMap) {
	editor.on("keyup", (e) => {
		if (e.key !== " ") return; // Option A: expand on space
		const map = getMap() || {};
		if (!Object.keys(map).length) return;

		const rng = editor.selection.getRng();
		const node = rng.startContainer;
		if (!node || node.nodeType !== 3) return; // must be a text node
		const caret = rng.startOffset;
		const data = node.data;
		// The space just typed sits at caret-1; the abbreviation is the run of
		// non-space characters immediately before it.
		const sp = caret - 1;
		if (sp < 0 || !/[\s ]/.test(data[sp])) return;
		let start = sp;
		while (start > 0 && !/[\s ]/.test(data[start - 1])) start--;
		const token = data.slice(start, sp);
		if (!token) return;

		const expansion = map[token.toLowerCase()];
		if (expansion == null) return; // no shortcut for this word

		// Replace the abbreviation (keeping the trailing space) in one undo step.
		editor.undoManager.transact(() => {
			node.replaceData(start, sp - start, expansion);
			const r = editor.dom.createRng();
			// caret goes just after the (still-present) trailing space
			const newCaret = start + expansion.length + (caret - sp);
			r.setStart(node, Math.min(newCaret, node.data.length));
			r.collapse(true);
			editor.selection.setRng(r);
		});
	});
}

function _esc(s) {
	return String(s == null ? "" : s)
		.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// A toolbar button that opens a reference list of the shortcuts, so drafters
// can discover them. `getList` returns the current [{abbreviation, expansion}].
export function registerShortcutsButton(editor, getList) {
	editor.ui.registry.addButton("ordershortcuts", {
		text: "Shortcuts",
		tooltip: "Typing shortcuts — type the abbreviation then space to expand",
		onAction: () => {
			const list = getList() || [];
			const rows = list.length
				? list.map((s) =>
					`<tr><td style="padding:4px 12px;font-weight:600;white-space:nowrap">${_esc(s.abbreviation)}</td>` +
					`<td style="padding:4px 12px">${_esc(s.expansion)}</td></tr>`).join("")
				: `<tr><td style="padding:10px" colspan="2">No shortcuts configured.</td></tr>`;
			editor.windowManager.open({
				title: "Order Drafting shortcuts",
				size: "normal",
				body: {
					type: "panel",
					items: [{
						type: "htmlpanel",
						html:
							'<p style="margin:0 0 10px;font-size:13px">Type the abbreviation followed by a ' +
							'<b>space</b> to expand it. Matching is case-insensitive; press ' +
							'<b>Ctrl+Z</b> to undo an expansion.</p>' +
							'<div style="max-height:340px;overflow:auto">' +
							'<table style="border-collapse:collapse;width:100%;font-size:13px">' +
							'<thead><tr>' +
							'<th style="text-align:left;padding:4px 12px;border-bottom:1px solid #ccc">Type</th>' +
							'<th style="text-align:left;padding:4px 12px;border-bottom:1px solid #ccc">Expands to</th>' +
							'</tr></thead><tbody>' + rows + '</tbody></table></div>',
					}],
				},
				buttons: [{ type: "cancel", text: "Close" }],
			});
		},
	});
}

/* ------------------------------------------------------------------ *
 * Tiptap
 * ------------------------------------------------------------------ */

/**
 * The same expand-on-space behaviour as attachShortcutExpansion, as a Tiptap
 * extension.
 *
 * Written as an input rule rather than a keyup handler: ProseMirror owns its
 * DOM, so the TinyMCE version's `node.replaceData` would be reverted or would
 * desynchronise the document. An input rule also gets the single-undo-step
 * behaviour for free, which the TinyMCE version had to arrange with
 * undoManager.transact.
 *
 * `getMap` is a function, not a value, so the editor can be created before the
 * shortcut list has finished loading - it is read on each expansion.
 */
export function createShortcutExpansion(getMap) {
	return Extension.create({
		name: "orderShortcuts",

		addInputRules() {
			return [
				new InputRule({
					// The word just completed by a space. The space is inside the
					// match so it can be preserved rather than re-typed.
					find: /(\S+) $/,
					handler: ({ state, range, match }) => {
						const map = (getMap && getMap()) || {};
						if (!Object.keys(map).length) return null;

						const expansion = map[String(match[1]).toLowerCase()];
						if (expansion == null) return null;

						state.tr.insertText(`${expansion} `, range.from, range.to);
						return undefined;
					},
				}),
			];
		},
	});
}
